import React, { useState, useEffect } from 'react';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  Search,
  Plus,
  Settings,
  X,
  Edit3,
  FolderOpen,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { documentosAPI } from '../services/api';

const AdminGestaoDocumentos = () => {
  const [documentos, setDocumentos] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');

  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadTipoId, setUploadTipoId] = useState('');
  const [uploadDescricao, setUploadDescricao] = useState('');
  const [uploading, setUploading] = useState(false);

  // Inline create tipo
  const [showNovoTipo, setShowNovoTipo] = useState(false);
  const [novoTipoNome, setNovoTipoNome] = useState('');
  const [novoTipoCor, setNovoTipoCor] = useState('#3498db');
  const [criandoTipo, setCriandoTipo] = useState(false);

  // Tipos modal (admin)
  const [showTiposModal, setShowTiposModal] = useState(false);
  const [editandoTipo, setEditandoTipo] = useState(null);
  const [formTipo, setFormTipo] = useState({
    nome: '',
    cor: '#3498db',
    descricao: '',
    ativo: true,
  });
  const [salvandoTipo, setSalvandoTipo] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [docsRes, tiposRes] = await Promise.all([
        documentosAPI.listarManuais(),
        documentosAPI.listarTipos('manual'),
      ]);
      setDocumentos(docsRes.data);
      setTiposDocumento(tiposRes.data);
    } catch (error) {
      toast.error('Erro ao carregar documentos');
    }
    setLoading(false);
  };

  // === UPLOAD ===
  const handleFileSelect = (e) => {
    setUploadFiles(Array.from(e.target.files));
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      toast.error('Selecione pelo menos um arquivo');
      return;
    }
    setUploading(true);
    try {
      for (const file of uploadFiles) {
        await documentosAPI.uploadManual(
          file,
          uploadTipoId || null,
          uploadDescricao || null
        );
      }
      toast.success(
        uploadFiles.length === 1
          ? 'Documento enviado com sucesso'
          : `${uploadFiles.length} documentos enviados com sucesso`
      );
      closeUploadModal();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar documento');
    }
    setUploading(false);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFiles([]);
    setUploadTipoId('');
    setUploadDescricao('');
    setShowNovoTipo(false);
    setNovoTipoNome('');
    setNovoTipoCor('#3498db');
  };

  // === CRIAR TIPO INLINE ===
  const handleCriarNovoTipo = async () => {
    if (!novoTipoNome.trim()) {
      toast.error('Digite o nome do tipo');
      return;
    }
    setCriandoTipo(true);
    try {
      const response = await documentosAPI.criarTipo({
        nome: novoTipoNome.trim(),
        categoria: 'manual',
        cor: novoTipoCor,
        extensoes_permitidas: 'pdf,doc,docx,xls,xlsx,ppt,pptx,jpg,jpeg,png',
        tamanho_max_mb: 20,
        ativo: true,
      });
      toast.success('Tipo criado com sucesso');
      setTiposDocumento([...tiposDocumento, response.data]);
      setUploadTipoId(response.data.id);
      setShowNovoTipo(false);
      setNovoTipoNome('');
      setNovoTipoCor('#3498db');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar tipo');
    }
    setCriandoTipo(false);
  };

  // === GERENCIAR TIPOS ===
  const openTiposModal = () => {
    setEditandoTipo(null);
    setFormTipo({ nome: '', cor: '#3498db', descricao: '', ativo: true });
    setShowTiposModal(true);
  };

  const closeTiposModal = () => {
    setShowTiposModal(false);
    setEditandoTipo(null);
    setFormTipo({ nome: '', cor: '#3498db', descricao: '', ativo: true });
  };

  const handleEditTipo = (tipo) => {
    setEditandoTipo(tipo);
    setFormTipo({
      nome: tipo.nome,
      cor: tipo.cor || '#3498db',
      descricao: tipo.descricao || '',
      ativo: tipo.ativo,
    });
  };

  const handleSaveTipo = async () => {
    if (!formTipo.nome.trim()) {
      toast.error('Nome e obrigatorio');
      return;
    }
    setSalvandoTipo(true);
    try {
      if (editandoTipo) {
        await documentosAPI.atualizarTipo(editandoTipo.id, {
          ...editandoTipo,
          ...formTipo,
          categoria: 'manual',
        });
        toast.success('Tipo atualizado');
      } else {
        const response = await documentosAPI.criarTipo({
          ...formTipo,
          categoria: 'manual',
          extensoes_permitidas: 'pdf,doc,docx,xls,xlsx,ppt,pptx,jpg,jpeg,png',
          tamanho_max_mb: 20,
        });
        toast.success('Tipo criado');
        setTiposDocumento([...tiposDocumento, response.data]);
      }
      loadData();
      if (!editandoTipo) {
        setFormTipo({ nome: '', cor: '#3498db', descricao: '', ativo: true });
      } else {
        setEditandoTipo(null);
        setFormTipo({ nome: '', cor: '#3498db', descricao: '', ativo: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar tipo');
    }
    setSalvandoTipo(false);
  };

  const handleDeleteTipo = async (tipo) => {
    if (!window.confirm(`Excluir tipo "${tipo.nome}"?`)) return;
    try {
      await documentosAPI.deletarTipo(tipo.id);
      toast.success('Tipo removido');
      setTiposDocumento(tiposDocumento.filter((t) => t.id !== tipo.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir tipo');
    }
  };

  // === DOCUMENTOS ===
  const handleDownload = async (doc) => {
    try {
      const response = await documentosAPI.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.nome_original);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar documento');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Excluir "${doc.nome_original}"?`)) return;
    try {
      await documentosAPI.deletarManual(doc.id);
      toast.success('Documento removido');
      setDocumentos(documentos.filter((d) => d.id !== doc.id));
    } catch (error) {
      toast.error('Erro ao remover documento');
    }
  };

  // === HELPERS ===
  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  // Contagem por tipo
  const contagemPorTipo = tiposDocumento.map((tipo) => ({
    ...tipo,
    count: documentos.filter((d) => d.tipo_documento_id === tipo.id).length,
  }));

  // Documentos filtrados
  const docsFiltrados = documentos.filter((doc) => {
    const matchBusca =
      !busca ||
      doc.nome_original.toLowerCase().includes(busca.toLowerCase()) ||
      (doc.descricao && doc.descricao.toLowerCase().includes(busca.toLowerCase()));
    const matchTipo = !filtroTipo || doc.tipo_documento_id === parseInt(filtroTipo);
    return matchBusca && matchTipo;
  });

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ color: 'var(--primary)' }}>
          <FileText size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Gestao de Documentos
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={openTiposModal}>
            <Settings size={16} />
            Tipos
          </button>
          <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
            <Upload size={16} />
            Upload
          </button>
        </div>
      </div>

      {/* Cards de tipos */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div
          className="search-box"
          style={{
            padding: '1rem 1.25rem',
            cursor: 'pointer',
            borderLeft: `4px solid var(--primary)`,
            background: !filtroTipo ? '#e8f5f3' : undefined,
            minWidth: '120px',
          }}
          onClick={() => setFiltroTipo('')}
        >
          <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>Todos</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
            {documentos.length}
          </div>
        </div>
        {contagemPorTipo.map((tipo) => (
          <div
            key={tipo.id}
            className="search-box"
            style={{
              padding: '1rem 1.25rem',
              cursor: 'pointer',
              borderLeft: `4px solid ${tipo.cor || '#3498db'}`,
              background: filtroTipo === String(tipo.id) ? '#e8f5f3' : undefined,
              minWidth: '120px',
            }}
            onClick={() => setFiltroTipo(String(tipo.id))}
          >
            <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>{tipo.nome}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: tipo.cor || '#3498db' }}>
              {tipo.count}
            </div>
          </div>
        ))}
      </div>

      {/* Busca */}
      <div className="search-box" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
          <input
            className="form-control"
            style={{ paddingLeft: '40px' }}
            placeholder="Buscar documentos..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>

      {/* Lista de documentos */}
      {docsFiltrados.length === 0 ? (
        <div className="search-box" style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-500)' }}>
          <FolderOpen size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <div style={{ fontSize: '1.1rem' }}>
            {busca || filtroTipo ? 'Nenhum documento encontrado' : 'Nenhum documento cadastrado'}
          </div>
          {!busca && !filtroTipo && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
              Clique em "Upload" para adicionar o primeiro documento
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {docsFiltrados.map((doc) => (
            <div
              key={doc.id}
              className="search-box"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem',
                borderLeft: `4px solid ${doc.tipo_documento_cor || '#3498db'}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: 0 }}>
                <FileText size={24} style={{ color: doc.tipo_documento_cor || '#3498db', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.nome_original}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
                    {doc.tipo_documento_nome && (
                      <span style={{ color: doc.tipo_documento_cor || '#3498db' }}>
                        {doc.tipo_documento_nome}
                      </span>
                    )}
                    <span>{formatFileSize(doc.tamanho_bytes)}</span>
                    <span>{formatDate(doc.criado_em)}</span>
                    {doc.uploader_nome && <span>por {doc.uploader_nome}</span>}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-secondary"
                  style={{ padding: '0.4rem 0.6rem' }}
                  onClick={() => handleDownload(doc)}
                  title="Baixar"
                >
                  <Download size={16} />
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ padding: '0.4rem 0.6rem', color: 'var(--danger)' }}
                  onClick={() => handleDelete(doc)}
                  title="Excluir"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* === MODAL UPLOAD === */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Enviar Documento</h3>
              <button className="modal-close" onClick={closeUploadModal}>&times;</button>
            </div>

            <div className="form-group">
              <label>Tipo de Documento</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select
                  className="form-control"
                  value={uploadTipoId}
                  onChange={(e) => setUploadTipoId(e.target.value)}
                  style={{ flex: 1 }}
                >
                  <option value="">Selecione (opcional)...</option>
                  {tiposDocumento.filter(t => t.ativo).map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>{tipo.nome}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-secondary"
                  title="Criar novo tipo"
                  onClick={() => setShowNovoTipo(!showNovoTipo)}
                >
                  {showNovoTipo ? <X size={16} /> : <Plus size={16} />}
                </button>
              </div>

              {showNovoTipo && (
                <div style={{ marginTop: '0.75rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 500, marginBottom: '0.5rem' }}>Criar novo tipo</div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      className="form-control"
                      placeholder="Nome do tipo"
                      value={novoTipoNome}
                      onChange={(e) => setNovoTipoNome(e.target.value)}
                      style={{ flex: 1 }}
                      autoFocus
                    />
                    <input
                      type="color"
                      value={novoTipoCor}
                      onChange={(e) => setNovoTipoCor(e.target.value)}
                      style={{ width: '36px', height: '36px', border: 'none', cursor: 'pointer', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                    <button
                      className="btn btn-primary"
                      onClick={handleCriarNovoTipo}
                      disabled={criandoTipo || !novoTipoNome.trim()}
                    >
                      {criandoTipo ? 'Criando...' : 'Criar e Selecionar'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Arquivo(s)</label>
              <div
                style={{
                  border: '2px dashed var(--gray-300)',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: uploadFiles.length > 0 ? '#f0f9ff' : 'transparent',
                }}
                onClick={() => document.getElementById('doc-upload-input').click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--primary)'; }}
                onDragLeave={(e) => { e.currentTarget.style.borderColor = 'var(--gray-300)'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = 'var(--gray-300)';
                  setUploadFiles(Array.from(e.dataTransfer.files));
                }}
              >
                <input id="doc-upload-input" type="file" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
                {uploadFiles.length === 0 ? (
                  <>
                    <Upload size={32} style={{ color: 'var(--gray-400)', marginBottom: '0.5rem' }} />
                    <div style={{ color: 'var(--gray-500)' }}>Clique ou arraste arquivos</div>
                  </>
                ) : (
                  <div>
                    {uploadFiles.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <FileText size={16} style={{ color: 'var(--primary)' }} />
                        <span style={{ fontSize: '0.9rem' }}>{f.name}</span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>({formatFileSize(f.size)})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Descricao</label>
              <textarea
                className="form-control"
                rows="2"
                value={uploadDescricao}
                onChange={(e) => setUploadDescricao(e.target.value)}
                placeholder="Descricao opcional..."
              />
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeUploadModal}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || uploadFiles.length === 0}>
                {uploading ? 'Enviando...' : (
                  <><Upload size={16} /> Enviar {uploadFiles.length > 1 ? `(${uploadFiles.length})` : ''}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === MODAL TIPOS === */}
      {showTiposModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '550px' }}>
            <div className="modal-header">
              <h3>Gerenciar Tipos</h3>
              <button className="modal-close" onClick={closeTiposModal}>&times;</button>
            </div>

            {/* Form novo/editar */}
            <div style={{ padding: '1rem', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                    {editandoTipo ? 'Editar tipo' : 'Novo tipo'}
                  </label>
                  <input
                    className="form-control"
                    placeholder="Nome do tipo"
                    value={formTipo.nome}
                    onChange={(e) => setFormTipo({ ...formTipo, nome: e.target.value })}
                  />
                </div>
                <input
                  type="color"
                  value={formTipo.cor}
                  onChange={(e) => setFormTipo({ ...formTipo, cor: e.target.value })}
                  style={{ width: '40px', height: '38px', border: 'none', cursor: 'pointer', borderRadius: '4px' }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={formTipo.ativo}
                    onChange={(e) => setFormTipo({ ...formTipo, ativo: e.target.checked })}
                  />
                  Ativo
                </label>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveTipo}
                  disabled={salvandoTipo || !formTipo.nome.trim()}
                >
                  {editandoTipo ? 'Salvar' : 'Criar'}
                </button>
                {editandoTipo && (
                  <button className="btn btn-secondary" onClick={() => {
                    setEditandoTipo(null);
                    setFormTipo({ nome: '', cor: '#3498db', descricao: '', ativo: true });
                  }}>
                    Cancelar
                  </button>
                )}
              </div>
            </div>

            {/* Lista de tipos */}
            <div style={{ padding: '1rem', maxHeight: '300px', overflowY: 'auto' }}>
              {tiposDocumento.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--gray-500)', padding: '1rem' }}>
                  Nenhum tipo cadastrado
                </div>
              ) : (
                tiposDocumento.map((tipo) => (
                  <div
                    key={tipo.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem',
                      borderBottom: '1px solid var(--gray-100)',
                      opacity: tipo.ativo ? 1 : 0.5,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: tipo.cor || '#3498db' }} />
                      <span style={{ fontWeight: 500 }}>{tipo.nome}</span>
                      {!tipo.ativo && <span style={{ fontSize: '0.75rem', color: 'var(--danger)' }}>(Inativo)</span>}
                      <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
                        {documentos.filter(d => d.tipo_documento_id === tipo.id).length} docs
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '0.3rem 0.5rem' }}
                        onClick={() => handleEditTipo(tipo)}
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '0.3rem 0.5rem', color: 'var(--danger)' }}
                        onClick={() => handleDeleteTipo(tipo)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeTiposModal}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGestaoDocumentos;
